/* eslint-disable id-length */
import { ChartData, ChartOptions, TooltipModel } from "chart.js";
import { merge } from "lodash";
import { forwardRef } from "react";

import reactRenderer from "app/lib/reactRenderer";
import {
  ForwardedChart,
  ForwardedChartHandle,
} from "app/components/shared/Chart/chart";
import { formatDuration } from "./Chart/utils";
import { verticalHoverLine } from "./Chart/plugins";

const findOrCreateTooltip = (canvas: HTMLCanvasElement) => {
  let tooltipEl =
    canvas?.parentElement?.querySelector<HTMLDivElement>("#chartjs-tooltip");
  if (canvas == null) {
    return null;
  }

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.className =
      "absolute bottom-0 left-0 z-100 pointer-events-none transform duration-100 shadow-sm translate-y-full";
    canvas.parentElement?.appendChild(tooltipEl);
  }

  return tooltipEl;
};

function createTooltipElement(
  TooltipComponent: React.FC<{ tooltip: TooltipModel<"line"> }>,
) {
  const renderTooltip = (context) => {
    const { tooltip, chart } = context;

    const tooltipEl = findOrCreateTooltip(chart.canvas);
    if (tooltipEl == null) {
      return;
    }

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    tooltipEl.style.opacity = "1";
    tooltipEl.style.transform = `translate(${tooltip.caretX}px,0)`;
    tooltipEl.style.top = `${chart.chartArea.height / 3}px`;
    tooltipEl.style.left = `4px`;

    reactRenderer(<TooltipComponent tooltip={tooltip} />, tooltipEl);
  };

  return renderTooltip;
}

export enum LineChartUnit {
  number = "number",
  percentage = "percentage",
  time = "time",
  duration = "duration",
}

interface Props {
  data: ChartData;
  options?: ChartOptions;
  units?: {
    x?: LineChartUnit;
    y?: LineChartUnit;
  };
  tooltip?: React.FC<{ tooltip: TooltipModel<"line"> }>;
}

const LineChart = forwardRef<ForwardedChartHandle, Props>(
  ({ data, options = {}, tooltip, units }, ref) => {
    if (tooltip) {
      options = {
        plugins: {
          tooltip: {
            enabled: false,
            mode: "index",
            external: createTooltipElement(tooltip),
          },
        },
      };
    }

    return (
      <div className="relative flex h-full">
        <ForwardedChart
          ref={ref}
          chartOptions={{
            type: "line",
            data,
            plugins: [verticalHoverLine],
            options: merge(
              {
                animation: false,
                datasets: {
                  line: {
                    tension: 0.25,
                    normalized: true,
                    pointRadius: 0,
                    borderWidth: 1.75,
                    spanGaps: true,
                  },
                },
                elements: {
                  point: {
                    radius: 0,
                    hoverRadius: 2,
                    borderWidth: 0.5,
                  },
                },
                interaction: {
                  mode: "index",
                  intersect: false,
                },
                scales: {
                  x: {
                    type: "time",
                    time: {
                      displayFormats: {
                        hour: "HH:mm",
                        minute: "HH:mm",
                      },
                    },
                    display: true,
                    grid: {
                      display: false,
                    },
                    ticks: {
                      maxRotation: 0,
                      autoSkipPadding: 16,
                      major: { enabled: true },
                      font: (context) => ({
                        weight: context.tick?.major ? "500" : "normal",
                      }),
                    },
                  },
                  y: {
                    type: "linear",
                    display: true,
                    grid: {
                      display: false,
                    },
                    beginAtZero: true,
                    ticks: {
                      stepSize: units?.y === LineChartUnit.percentage && 25,
                      autoSkipPadding: 8,
                      includeBounds: true,
                      maxRotation: 0,
                      callback(value: number) {
                        switch (units?.y) {
                          case LineChartUnit.percentage:
                            return `${value}%`;
                          case LineChartUnit.duration:
                            return formatDuration(value);
                          case LineChartUnit.number:
                          default:
                            return value;
                        }
                      },
                    },
                  },
                },
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                },
                beginAtZero: true,
              },
              options,
            ),
          }}
        />
      </div>
    );
  },
);

export default LineChart;
